/**
 * The ground under a prop: where it stands and which way the ground faces there.
 *
 * `getSurfaceRadius` already puts a point exactly on the planet mesh (an exact ray–triangle hit on the
 * same displaced facets the renderer draws). What props lacked was the *tilt*: rocks and trees were
 * stood along the radial direction, so on a facet tilted 10–20° one side floated and the other sank.
 * This gives the facet's own normal, and for a prop wider than a facet a normal fitted across its
 * footprint, plus how much the ground disagrees with itself under it (`spread`), which the renderer
 * turns into how deep to sink the prop so its base is under the ground on every side.
 *
 * Presentation only: nothing in the simulation reads it, so it changes no golden and needs no kernel
 * twin. Pure and deterministic all the same (dmath for the angles), so a sheet or a test gets the same
 * frame as the game.
 */

import type { PlanetState, Vec3 } from '../types';
import { getSurfaceRadiusRaw, lastSurfaceTriangle } from './surface';
import { dsin, dcos, dacos } from './dmath';

export type GroundPlanet = Pick<PlanetState, 'heightmap' | 'basePositions' | 'radius'>;

export interface GroundFrame {
  /** Distance from the planet's centre to the ground at `dir` (the mesh surface). */
  radius: number;
  /** The ground's outward unit normal: the facet's under a narrow prop, a fit across the footprint under a wide one. */
  normal: Vec3;
  /** The widest angle (radians) between `normal` and any facet sampled under the footprint; 0 when one facet carries it all. */
  spread: number;
  /** The mesh facet under `dir`, or -1 when the sampler fell back to a vertex (non-mesh data). */
  triangle: number;
}

/** Samples around the footprint's rim (plus the centre). Six keeps a wide rock's fit cheap and symmetric. */
const RIM_SAMPLES = 6;

/** The outward unit normal of mesh facet `t`, from its three displaced vertices (base + unit(base) × height). */
export function facetNormal(planet: GroundPlanet, t: number): Vec3 {
  const bp = planet.basePositions, hm = planet.heightmap;
  const p: number[] = [];
  for (let k = 0; k < 3; k++) {
    const vi = t * 3 + k;
    const x = bp[vi * 3], y = bp[vi * 3 + 1], z = bp[vi * 3 + 2];
    const len = Math.sqrt(x * x + y * y + z * z);
    const s = len > 0 ? 1 + hm[vi] / len : 1;
    p.push(x * s, y * s, z * s);
  }
  const e1x = p[3] - p[0], e1y = p[4] - p[1], e1z = p[5] - p[2];
  const e2x = p[6] - p[0], e2y = p[7] - p[1], e2z = p[8] - p[2];
  let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
  const nl = Math.sqrt(nx * nx + ny * ny + nz * nz);
  if (nl < 1e-18) return radialOf(p[0] + p[3] + p[6], p[1] + p[4] + p[7], p[2] + p[5] + p[8]);
  nx /= nl; ny /= nl; nz /= nl;
  // Outward: the mesh winding isn't relied on; the centroid points out of the planet.
  const cx = p[0] + p[3] + p[6], cy = p[1] + p[4] + p[7], cz = p[2] + p[5] + p[8];
  if (nx * cx + ny * cy + nz * cz < 0) { nx = -nx; ny = -ny; nz = -nz; }
  return { x: nx, y: ny, z: nz };
}

function radialOf(x: number, y: number, z: number): Vec3 {
  const l = Math.sqrt(x * x + y * y + z * z);
  return l > 0 ? { x: x / l, y: y / l, z: z / l } : { x: 0, y: 1, z: 0 };
}

/**
 * The ground frame at `dir` for a prop whose footprint reaches `footprint` world units from its centre.
 * With `footprint` 0 the frame is the one facet under the centre. Wider props sample the rim too and
 * take the mean facet normal, so a boulder straddling a ridge sits across it instead of on one side.
 */
export function groundFrame(planet: GroundPlanet, dir: Vec3, footprint = 0): GroundFrame {
  const d = radialOf(dir.x, dir.y, dir.z);
  // The raw components, as getSurfaceRadius takes them: the sampler normalises once, so the radius is bit-identical to its answer
  const radius = getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, dir.x, dir.y, dir.z);
  const t0 = lastSurfaceTriangle();
  if (t0 < 0) return { radius, normal: d, spread: 0, triangle: -1 };
  const n0 = facetNormal(planet, t0);
  if (!(footprint > 0) || !(planet.radius > 0)) return { radius, normal: n0, spread: 0, triangle: t0 };

  // Rim samples: directions `footprint / radius` radians from `d`, around a tangent basis.
  const ref = Math.abs(d.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  let t1x = d.y * ref.z - d.z * ref.y, t1y = d.z * ref.x - d.x * ref.z, t1z = d.x * ref.y - d.y * ref.x;
  const l1 = Math.sqrt(t1x * t1x + t1y * t1y + t1z * t1z); t1x /= l1; t1y /= l1; t1z /= l1;
  const t2x = d.y * t1z - d.z * t1y, t2y = d.z * t1x - d.x * t1z, t2z = d.x * t1y - d.y * t1x;
  const a = footprint / planet.radius;
  const sa = dsin(a), ca = dcos(a);
  const normals: Vec3[] = [n0];
  for (let k = 0; k < RIM_SAMPLES; k++) {
    const ph = (2 * Math.PI * k) / RIM_SAMPLES;
    const cp = dcos(ph), sp = dsin(ph);
    const sx = d.x * ca + (t1x * cp + t2x * sp) * sa;
    const sy = d.y * ca + (t1y * cp + t2y * sp) * sa;
    const sz = d.z * ca + (t1z * cp + t2z * sp) * sa;
    getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, sx, sy, sz);
    const t = lastSurfaceTriangle();
    if (t >= 0 && t !== t0 && !normals.some((_, i) => i > 0 && sampleTriangles[i - 1] === t)) {
      sampleTriangles.push(t);
      normals.push(facetNormal(planet, t));
    }
  }
  sampleTriangles.length = 0;
  let mx = 0, my = 0, mz = 0;
  for (const n of normals) { mx += n.x; my += n.y; mz += n.z; }
  const ml = Math.sqrt(mx * mx + my * my + mz * mz);
  const normal = ml > 1e-12 ? { x: mx / ml, y: my / ml, z: mz / ml } : n0;
  let spread = 0;
  for (const n of normals) {
    const c = Math.max(-1, Math.min(1, normal.x * n.x + normal.y * n.y + normal.z * n.z));
    const ang = dacos(c);
    if (ang > spread) spread = ang;
  }
  return { radius, normal, spread, triangle: t0 };
}

/** Scratch: the distinct rim facets of the current call (a facet counts once however many samples land on it). */
const sampleTriangles: number[] = [];

export interface GroundRelief {
  /** The ground's radius under the centre. */
  radius: number;
  /** How far the ground rises above the centre's radius anywhere on the footprint's rim (≥ 0). */
  rise: number;
  /** How far it drops below it (≥ 0). */
  drop: number;
}

/**
 * How uneven the ground is under a footprint: the highest and lowest ground on its rim, relative to the centre.
 * A building sits level on a pad whose top is at the rise and whose foot reaches below the drop.
 */
export function groundRelief(planet: GroundPlanet, dir: Vec3, footprint: number): GroundRelief {
  const d = radialOf(dir.x, dir.y, dir.z);
  const radius = getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, dir.x, dir.y, dir.z);
  if (!(footprint > 0) || !(planet.radius > 0)) return { radius, rise: 0, drop: 0 };
  const ref = Math.abs(d.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
  let t1x = d.y * ref.z - d.z * ref.y, t1y = d.z * ref.x - d.x * ref.z, t1z = d.x * ref.y - d.y * ref.x;
  const l1 = Math.sqrt(t1x * t1x + t1y * t1y + t1z * t1z); t1x /= l1; t1y /= l1; t1z /= l1;
  const t2x = d.y * t1z - d.z * t1y, t2y = d.z * t1x - d.x * t1z, t2z = d.x * t1y - d.y * t1x;
  const a = footprint / planet.radius;
  const sa = dsin(a), ca = dcos(a);
  let rise = 0, drop = 0;
  for (let k = 0; k < RIM_SAMPLES * 2; k++) {
    const ph = (2 * Math.PI * k) / (RIM_SAMPLES * 2);
    const cp = dcos(ph), sp = dsin(ph);
    const sx = d.x * ca + (t1x * cp + t2x * sp) * sa;
    const sy = d.y * ca + (t1y * cp + t2y * sp) * sa;
    const sz = d.z * ca + (t1z * cp + t2z * sp) * sa;
    // The rim point's height above the centre's base plane: its radius along its own direction, projected onto d
    const r = getSurfaceRadiusRaw(planet.heightmap, planet.basePositions, planet.radius, sx, sy, sz);
    const h = r * (sx * d.x + sy * d.y + sz * d.z) - radius;
    if (h > rise) rise = h;
    if (-h > drop) drop = -h;
  }
  return { radius, rise, drop };
}

/** The angle (radians) between the ground normal and the radial direction: how tilted the ground is under `dir`. */
export function groundTilt(frame: GroundFrame, dir: Vec3): number {
  const d = radialOf(dir.x, dir.y, dir.z);
  const c = Math.max(-1, Math.min(1, frame.normal.x * d.x + frame.normal.y * d.y + frame.normal.z * d.z));
  return dacos(c);
}
